import * as Mui from "@mui/material";
import { useFormik } from "formik";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { setSnackbar } from "../../../../../util/state/globalAction";
import { GlobalContext } from "../../../../../util/state/GlobalContextProvider";
import { snackbarTypes } from "../../../../../util/state/globalVariables";
import AutoComplete from "../../common/AutoComplete";
import { nameof } from "../../common/ObjectUtils";
import {
  SupplierCustomGroupLocationsRequests,
  SupplierCustomGroupService,
} from "../service/supplierCustomGroupLocations.actions";
import { useTranslation } from "react-i18next";
import EnumChangeName from "../../common/EnumChangeName";

interface Props {
  tenant: string;
  isOpen: boolean;
  onClose: () => void;
  groupLocationsId?: number;
  onUpdateDatasource?: any;
  data: any;
}

const GroupLocationsDialog = (props: Props) => {
  const { dispatchGlobalData } = useContext(GlobalContext);
  const enumTypeName = EnumChangeName();
  const refForm = useRef<HTMLFormElement>(null);

  const [locations, setLocations] = useState([]);

  const { t } = useTranslation(["operation"]);

  const onClose = () => {
    props.onClose();
    formik.resetForm();
  };
  const onSubmit = (data: SupplierCustomGroupLocationsRequests.Create) => {
    const updateData = {
      locationCustomGroupMessage: {
        id: props.groupLocationsId,
        name: data.name,
        desc: data.description,
        locations: locations.filter((loc: any) =>
          formik.values.locations.includes(loc.id)
        ),
      },
    };
    if (!isEdit) {
      SupplierCustomGroupService.create
        .groupLocations(props.tenant, updateData as any)
        .then(
          (response) => {
            props.onClose();
            formik.resetForm();

            dispatchGlobalData({
              type: setSnackbar,
              payload: {
                snackbar: {
                  message: t("supplierCustomGroupLocation.succesfull"),
                  type: snackbarTypes.success,
                  show: true,
                  autoClose: true,
                },
              },
            });

            props.onUpdateDatasource && props.onUpdateDatasource();
          },
          () => {
            props.onClose();
            formik.resetForm();

            dispatchGlobalData({
              type: setSnackbar,
              payload: {
                snackbar: {
                  message: t("supplierCustomGroupLocation.errorCreate"),
                  type: snackbarTypes.error,
                  show: true,
                  autoClose: true,
                },
              },
            });
          }
        );
    } else if (isEdit && props.groupLocationsId) {
      SupplierCustomGroupService.update
        .groupLocations(props.tenant, props.groupLocationsId, updateData as any)
        .then(
          (response) => {
            props.onClose();
            formik.resetForm();
            dispatchGlobalData({
              type: setSnackbar,
              payload: {
                snackbar: {
                  message: t("supplierCustomGroupLocation.updated"),
                  type: snackbarTypes.success,
                  show: true,
                  autoClose: true,
                },
              },
            });
            props.onUpdateDatasource && props.onUpdateDatasource(response);
          },
          () => {
            props.onClose();
            formik.resetForm();
            dispatchGlobalData({
              type: setSnackbar,
              payload: {
                snackbar: {
                  message: t("supplierCustomGroupLocation.errorUpdate"),
                  type: snackbarTypes.error,
                  show: true,
                  autoClose: true,
                },
              },
            });
          }
        );
    }
  };

  const formik = useFormik<SupplierCustomGroupLocationsRequests.Create>({
    initialValues: {
      name: "",
      description: "",
      locations: [],
    },
    onSubmit: onSubmit,
  });

  useEffect(() => {
    if (props.isOpen) {
      if (props.groupLocationsId) {
        formik.setValues({
          name: props.data.name,
          description: props.data.desc,
          locations: props.data.locations.map((loc: any) => loc.id),
        });
      }

      SupplierCustomGroupService.get
        .groupLocationsById(props.tenant, props.groupLocationsId as any, {
          pageNumber: 0,
          pageSize: 99999,
        })
        .then((response: any) => {
          let allLocations = [
            ...response.content.map((loc: any) => ({
              ...loc,
              label: `${loc.locationDesc} (${enumTypeName.locationType(
                loc.locationType
              )})`,
              value: loc.id,
            })),
          ];

          if (props.groupLocationsId) {
            allLocations = [
              ...allLocations,
              ...props.data.locations.map((loc: any) => ({
                ...loc,
                label: `${loc.locationDesc} (${enumTypeName.locationType(
                  loc.locationType
                )})`,
                value: loc.id,
              })),
            ];
          }

          setLocations(allLocations as any);
        });
    }
  }, [props.isOpen]);

  const isEdit = useMemo(
    () => (props.groupLocationsId ? true : false),
    [props.groupLocationsId]
  );
  return (
    <Mui.Dialog
      open={props.isOpen}
      onClose={onClose}
      fullWidth={true}
      maxWidth="md"
    >
      <Mui.DialogTitle>
        {!isEdit ? (
          <Mui.Typography variant="h5">
            {t("supplierCustomGroupLocation.modal.createTitle")}
          </Mui.Typography>
        ) : (
          <Mui.Typography variant="h5">
            {t("supplierCustomGroupLocation.modal.editTitle")}
          </Mui.Typography>
        )}
      </Mui.DialogTitle>

      <Mui.DialogContent>
        <form ref={refForm} onSubmit={formik.handleSubmit}>
          <Mui.Grid container spacing="16px">
            <Mui.Grid item md={4} xs={12}>
              <Mui.TextField
                name={nameof<SupplierCustomGroupLocationsRequests.Create>(
                  "name"
                )}
                label={t("supplierCustomGroupLocation.modal.name")}
                fullWidth={true}
                onChange={formik.handleChange}
                value={formik.values.name}
              />
            </Mui.Grid>

            <Mui.Grid item md={4} xs={12}>
              <Mui.TextField
                name={nameof<SupplierCustomGroupLocationsRequests.Create>(
                  "description"
                )}
                label={t("supplierCustomGroupLocation.modal.description")}
                fullWidth={true}
                onChange={formik.handleChange}
                value={formik.values.description}
              />
            </Mui.Grid>

            <Mui.Grid item md={4} xs={12}>
              <AutoComplete
                multiple={true}
                options={locations}
                label={t("supplierCustomGroupLocation.modal.location")}
                value={formik.values.locations}
                onChange={(value) =>
                  formik.setFieldValue(
                    nameof<SupplierCustomGroupLocationsRequests.Create>(
                      "locations"
                    ),
                    value
                  )
                }
                fullWidth={true}
              />
            </Mui.Grid>
          </Mui.Grid>
        </form>
      </Mui.DialogContent>

      <Mui.DialogActions>
        <Mui.Button onClick={onClose} variant="outlined">
          {t("supplierCustomGroupLocation.modal.cancel")}
        </Mui.Button>
        <Mui.Button type="submit" onClick={() => formik.submitForm()}>
          {t("supplierCustomGroupLocation.modal.save")}
        </Mui.Button>
      </Mui.DialogActions>
    </Mui.Dialog>
  );
};

export default GroupLocationsDialog;
